import cn from "classnames";
import { memo, useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { A11yRoot } from "../../../a11y";
import { useSelectMode } from "../../hooks";
import { OptionType } from "../../types";
import { VirtualList } from "../virtual-list";
import { useSelectContext } from "../../context";
import { Option } from "./option";
import { OptionsProps } from "./types";
import { NotFoundLabel } from "./not-found-label";
import { useOptionsA11y } from "./hooks";
import styles from "./styles.module.scss";

export const Options = memo(
  ({
    dataTestId,
    type,
    options,
    searchable,
    searchValue,
    notFoundLabel,
    footer,
    onClick,
  }: OptionsProps) => {
    const { listboxId, setKeyboardFocusedOptionIndex } = useSelectContext();

    const { isMultipleMode } = useSelectMode(type);

    const handleClick = (option: OptionType) => () => onClick(option);

    const parentRef = useRef<HTMLDivElement>(null);

    const hasNotSearchResult =
      searchable && Boolean(searchValue.length) && !options.length;

    const { measureElement, getVirtualItems, getTotalSize, scrollToIndex } =
      useVirtualizer({
        count: options.length,
        estimateSize: () => 45,
        getScrollElement: () => parentRef.current,
      });

    const items = getVirtualItems();

    const {
      hasA11yFocused,
      a11yCurrentChildIndex,
      resetFocus,
      handleArrowUp,
      handleArrowDown,
      handleEnterKeyDown,
    } = useOptionsA11y({
      count: options.length,
      scrollToIndex,
    });

    useEffect(() => {
      if (hasA11yFocused) {
        setKeyboardFocusedOptionIndex(a11yCurrentChildIndex);
      } else {
        setKeyboardFocusedOptionIndex(undefined);
      }
    }, [a11yCurrentChildIndex, hasA11yFocused, setKeyboardFocusedOptionIndex]);

    if (hasA11yFocused && a11yCurrentChildIndex > -1) {
      scrollToIndex(a11yCurrentChildIndex);
    }

    const handleAfterBlur = () => {
      if (hasNotSearchResult) {
        return;
      }

      resetFocus();
    };

    return (
      <A11yRoot
        onEnter={() => {
          if (hasA11yFocused) {
            onClick(options[a11yCurrentChildIndex]);
          }

          handleEnterKeyDown();
        }}
        onArrowUp={handleArrowUp}
        onArrowDown={handleArrowDown}
        onAfterBlur={handleAfterBlur}
      >
        <div
          className={cn(styles["list-wrapper"], {
            [styles["with-footer"]]: footer,
          })}
        >
          {hasNotSearchResult && notFoundLabel && (
            <NotFoundLabel text={notFoundLabel} dataTestId={dataTestId} />
          )}
          <VirtualList
            ref={parentRef}
            className={cn({
              [styles["no-padding"]]: hasNotSearchResult && !notFoundLabel,
            })}
            dataTestId={dataTestId}
            topOffset={`${items[0]?.start}px`}
            totalHeight={getTotalSize()}
          >
            {items.map(({ index }) => {
              const option = options[index];

              return (
                <Option
                  hasA11yFocus={a11yCurrentChildIndex === index}
                  key={index}
                  option={option}
                  onClick={handleClick(option)}
                  searchable={searchable}
                  searchValue={searchValue}
                  showCheckbox={isMultipleMode}
                  dataTestId={`${dataTestId}-item`}
                  index={index}
                  ref={measureElement}
                  id={`${listboxId}-option-${index}`}
                />
              );
            })}
          </VirtualList>

          {footer && (
            <div
              data-test-id={`${dataTestId}-footer`}
              className={styles.footer}
            >
              {footer}
            </div>
          )}
        </div>
      </A11yRoot>
    );
  }
);
